import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import WhatsAppFloat from '../WhatsAppFloat';
import { createPublicBooking } from '../../services/maps';
import { getQuote, clearQuote, saveBooking } from '../../utils/session';
import './Confirmation.css';

const PHONE_REGEX = /^[6-9]\d{9}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const formatDate = (raw) => {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatTime = (raw) => {
  if (!raw) return '';
  if (/^\d{2}:\d{2}$/.test(raw)) {
    const [h, m] = raw.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hh = h % 12 || 12;
    return `${hh}:${String(m).padStart(2, '0')} ${ampm}`;
  }
  return raw;
};

const Collapsible = ({ title, children, defaultOpen }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="collapsible">
      <div className="collapsible-header" onClick={() => setOpen((o) => !o)}>
        <span>{title}</span>
        <span className="collapsible-arrow">{open ? '▲' : '▼'}</span>
      </div>
      {open && <div className="collapsible-content">{children}</div>}
    </div>
  );
};

const Confirmation = () => {
  const navigate = useNavigate();
  const [quote] = useState(() => getQuote());

  const [customer, setCustomer] = useState({
    name: '',
    phone: quote?.customer?.phone || '',
    email: quote?.customer?.email || '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!quote) navigate('/', { replace: true });
  }, [quote, navigate]);

  if (!quote) return null;

  const trip = quote.trip || {};
  const fare = quote.fare || {};
  const origin = trip.origin || {};
  const destination = trip.destination || {};
  const vehicleLabel = trip.vehicle?.label || trip.vehicleType || quote.vehicleType || '—';
  const total = fare.total ?? 0;
  const advancePercent = fare.advancePercent ?? 20;
  const advanceAmount = fare.advanceAmount ?? Math.round((total * advancePercent) / 100);
  const balanceAmount = fare.balanceAmount ?? Math.max(0, total - advanceAmount);

  const validate = () => {
    const next = {};
    if (!PHONE_REGEX.test(customer.phone.trim())) next.phone = 'Enter a valid 10-digit mobile number';
    if (customer.email && !EMAIL_REGEX.test(customer.email.trim())) next.email = 'Invalid email';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleConfirmBooking = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        quoteId: quote.quoteId,
        customerName: customer.name.trim() || undefined,
        customerPhone: customer.phone.trim(),
        customerEmail: customer.email.trim() || undefined,
        passengers: Number(quote.passengers) || 1,
      };
      const result = await createPublicBooking(payload);
      const bookingCode = result?.bookingCode || result?.booking?.bookingCode;
      if (!bookingCode) throw new Error('Booking succeeded but no code returned');
      saveBooking({
        bookingCode,
        booking: result.booking || null,
        payment: result.payment || null,
        whatsapp: result.whatsapp || null,
      });
      clearQuote();
      navigate(`/payment/${bookingCode}`);
    } catch (error) {
      const apiErr = error?.response?.data?.error;
      if (apiErr === 'QUOTE_EXPIRED' || apiErr === 'QUOTE_ALREADY_USED') {
        toast.error('Quote expired. Please get a new estimate.');
        clearQuote();
        navigate('/', { replace: true });
        return;
      }
      const serverMessage =
        error?.response?.data?.errors?.[0]?.msg ||
        error?.response?.data?.message ||
        'Could not confirm your booking. Please try again.';
      toast.error(serverMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const passengers = Number(quote.passengers) || 1;

  return (
    <div className="confirmation-container">
      <div className="confirmation-card">
        <div className="confirmation-summary">
          <div className="summary-row">
            <span className="summary-label from">From</span>
            <span className="summary-value">{origin.address || '—'}</span>
          </div>
          <div className="summary-row">
            <span className="summary-label to">To</span>
            <span className="summary-value">{destination.address || '—'}</span>
          </div>
          <div className="summary-row">
            <span className="summary-label">Departure</span>
            <span className="summary-value">
              {formatDate(trip.travelDate)} {formatTime(trip.pickupTime)}
            </span>
          </div>
        </div>

        <div className="confirmation-vehicle-details">
          <div className="vehicle-title">{vehicleLabel} Cab</div>
          <div className="vehicle-info">{passengers} Passenger{passengers > 1 ? 's' : ''}</div>
          <div className="confirmation-fare-card">
            <div className="fare-row">
              <span>Total fare</span>
              <span className="fare-amount">{formatINR(total)}</span>
            </div>
            <div className="fare-meta">Advance ({advancePercent}%): {formatINR(advanceAmount)}</div>
            <div className="fare-meta">Balance on arrival: {formatINR(balanceAmount)}</div>
          </div>
        </div>

        <div className="customer-info-form" style={{ marginTop: '1.5rem' }}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '1.05rem' }}>Your details</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            <input
              type="text"
              placeholder="Full name (optional)"
              value={customer.name}
              onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))}
              style={inputStyle(errors.name)}
            />
            <input
              type="tel"
              inputMode="numeric"
              placeholder="10-digit mobile number"
              value={customer.phone}
              onChange={(e) =>
                setCustomer((c) => ({ ...c, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))
              }
              style={inputStyle(errors.phone)}
            />
            {errors.phone && <span style={errorStyle}>{errors.phone}</span>}
            <input
              type="email"
              placeholder="Email (optional)"
              value={customer.email}
              onChange={(e) => setCustomer((c) => ({ ...c, email: e.target.value }))}
              style={inputStyle(errors.email)}
            />
            {errors.email && <span style={errorStyle}>{errors.email}</span>}
          </div>

          <div style={{ marginTop: '1rem' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#12100E', marginBottom: 6 }}>
              Payment
            </div>
            <div style={paymentPillStyle}>
              <span>📲 UPI advance ({formatINR(advanceAmount)}) — balance {formatINR(balanceAmount)} to driver on arrival</span>
            </div>
          </div>

          <div style={disclaimerStyle}>
            By confirming, you agree to our cancellation policy. You'll pay the advance via UPI on the next step.
          </div>

          <button
            className="confirm-booking-btn"
            onClick={handleConfirmBooking}
            disabled={submitting}
            style={{
              width: '100%',
              marginTop: '1rem',
              padding: '0.85rem 1rem',
              background: submitting ? '#ffaa80' : '#E8590A',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontWeight: 700,
              fontSize: '1.05rem',
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? 'Confirming…' : `Confirm & Pay Advance — ${formatINR(advanceAmount)} →`}
          </button>
        </div>

        <div className="confirmation-collapsibles">
          <Collapsible title="Rules & Restrictions" defaultOpen>
            <ul className="rules-list">
              <li>Excludes toll tax, parking fee, state fee</li>
              <li>GST is included in this fare</li>
              <li>{advancePercent}% advance required to confirm the ride</li>
              <li>Patna pickup: min 4 hrs advance · Others: 12 hrs advance</li>
            </ul>
          </Collapsible>
          <Collapsible title="Ride Inclusions">
            <ul className="rules-list">
              <li>Driver allowance</li>
              <li>Fuel charges</li>
              <li>Basic vehicle insurance</li>
              <li>All-India permit</li>
            </ul>
          </Collapsible>
          <Collapsible title="Cancellation Policy">
            <ul className="rules-list">
              <li>Free cancellation up to 24 hours before departure</li>
              <li>50% refund if cancelled 12-24 hours before departure</li>
              <li>No refund if cancelled within 12 hours of departure</li>
            </ul>
          </Collapsible>
        </div>
      </div>
      <WhatsAppFloat />
    </div>
  );
};

const inputStyle = (hasError) => ({
  width: '100%',
  padding: '0.7rem 0.9rem',
  borderRadius: 8,
  border: `1px solid ${hasError ? '#e74c3c' : '#d0d0d0'}`,
  fontSize: '1rem',
  outline: 'none',
});

const errorStyle = { color: '#e74c3c', fontSize: '0.85rem' };

const paymentPillStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 12px',
  border: '1px solid rgba(18,16,14,0.15)',
  borderRadius: 8,
  fontSize: 14,
  cursor: 'pointer',
};

const disclaimerStyle = {
  marginTop: '0.75rem',
  padding: '0.6rem 0.85rem',
  background: '#FFF7ED',
  border: '1px solid #FED7AA',
  borderRadius: 8,
  color: '#9A3412',
  fontSize: 13,
};

export default Confirmation;
